import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, type DayPickerProps } from "react-day-picker";
import { it } from "date-fns/locale";
import { cn } from "../lib/cn";

/**
 * Calendar — shadcn-style wrapper around `react-day-picker` v9, themed
 * with the Ando palette (cream / ink / ando-orange).
 *
 * Used by `DatePickerInput` (see `ando/ActionFieldEditor`) to replace
 * the native `<input type="date">` inside HITL action forms — the
 * `create_event` proposal, scheduled tasks, due-date fields, etc.
 *
 * Default locale is Italian, Monday-anchored. Override `locale` /
 * `weekStartsOn` per consumer (e.g. an enterprise tenant on the EN
 * shell). Passes any extra DayPicker prop through (modifiers, disabled,
 * fromYear, captionLayout, …) so future surfaces can lean on the same
 * primitive without forking it.
 */
export type CalendarProps = DayPickerProps;

/** Nav all'indietro sempre possibile: senza un `startMonth` esplicito v9 lo
 *  deriva da `disabled.before` e spegne la freccia precedente. I giorni
 *  passati restano comunque non selezionabili tramite `disabled`. */
const DEFAULT_NAV_START = new Date(1900, 0, 1);

export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  locale = it,
  weekStartsOn = 1,
  startMonth = DEFAULT_NAV_START,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      locale={locale}
      weekStartsOn={weekStartsOn}
      startMonth={startMonth}
      // ``relative`` anchors the absolutely-positioned month ``nav`` (prev /
      // next chevrons) to the calendar itself. Without it the chevrons escape
      // to the nearest positioned ancestor — e.g. when the calendar is nested
      // below other content in a popover (the email "Programma invio" panel),
      // the arrows flew to the top of the popover instead of flanking the
      // month caption.
      className={cn("relative p-2", className)}
      classNames={{
        months: "flex flex-col sm:flex-row gap-2",
        month: "flex flex-col gap-3",
        // Caption e nav condividono la stessa altezza e lo stesso top: il nav
        // è fratello del mese (v9), quindi senza questo le frecce restano
        // qualche pixel sopra il titolo.
        month_caption: "relative flex h-8 items-center justify-center",
        caption_label: "text-sm font-medium text-ink-1 tabular-nums",
        nav: "absolute inset-x-0 top-2 z-10 flex h-8 items-center justify-between gap-1 px-1",
        button_previous: cn(
          "grid h-7 w-7 cursor-pointer place-items-center rounded-md text-ink-2",
          "transition-colors hover:bg-black/[0.04] hover:text-ink-1",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
          "disabled:pointer-events-none disabled:opacity-40",
        ),
        button_next: cn(
          "grid h-7 w-7 cursor-pointer place-items-center rounded-md text-ink-2",
          "transition-colors hover:bg-black/[0.04] hover:text-ink-1",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
          "disabled:pointer-events-none disabled:opacity-40",
        ),
        month_grid: "w-full border-collapse",
        weekdays: "flex",
        weekday:
          "w-9 text-[10px] font-medium text-ink-3 uppercase tracking-wide tabular-nums",
        week: "flex w-full mt-1",
        day: "h-9 w-9 p-0 text-center text-sm tabular-nums focus-within:relative focus-within:z-10",
        day_button: cn(
          "h-9 w-9 rounded-md p-0 font-normal text-ink-1",
          "transition-colors hover:bg-ando-orange-soft/60 hover:text-ink-1",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
          "aria-selected:opacity-100",
        ),
        selected: cn(
          "[&>button]:rounded-full [&>button]:bg-ando-orange [&>button]:text-white",
          "[&>button]:hover:bg-ando-orange [&>button]:hover:text-white",
          "[&>button]:focus:bg-ando-orange [&>button]:focus:text-white",
          "[&>button]:font-semibold",
        ),
        today: "[&>button]:ring-1 [&>button]:ring-ando-orange/45",
        outside: "[&>button]:text-ink-3 [&>button]:opacity-50",
        disabled: "[&>button]:text-ink-3 [&>button]:opacity-40",
        // La fascia dell'intervallo sta sulla CELLA, il pieno arancione solo
        // sui due estremi. In `mode="range"` react-day-picker marca `selected`
        // OGNI giorno del range: senza il `!bg-transparent` qui sotto la regola
        // `selected` dipinge di arancione pieno tutta la fascia — testo bianco
        // bold per quaranta giorni di fila, e hover ancora piu' acceso.
        range_start: "rounded-l-full bg-ando-orange-soft/45",
        range_end: "rounded-r-full bg-ando-orange-soft/45",
        range_middle: cn(
          "bg-ando-orange-soft/45",
          "[&>button]:!bg-transparent [&>button]:!font-normal [&>button]:!text-ink-1",
          "[&>button]:!opacity-100 [&>button]:hover:!bg-ando-orange-soft/80",
        ),
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className: cls, ...rest }) => {
          const Icon = orientation === "left" ? ChevronLeft : ChevronRight;
          return <Icon className={cn("h-4 w-4", cls)} {...rest} />;
        },
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";
